import { faPlus } from '@fortawesome/pro-regular-svg-icons';
import { faCaretDown } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Button,
  ClickAwayListener,
  Divider,
  Paper,
  Popper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import React, { useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';

import useConfirm from '../../hooks/useConfirm';
import useModal from '../../hooks/useModal';
import { SheetNameSchema } from '../../validationSchemas/ReportTemplateSchema';
import FormEndOptions from '../forms/FormEndOptions';
import SectionsPreview from './SectionsPreview';
import { useReportingHubContext } from './useReportingHubContext';

function Sheets() {
  const form = useFormContext();
  const { setActiveSheet } = useReportingHubContext();
  const { setValue, watch } = form;
  const setValueOptions = {
    shouldDirty: true,
    shouldValidate: true,
  };

  const sheetsTabOptionsRef = useRef([]);

  const [sheetTabAnchorIndex, setSheetTabAnchorIndex] = useState<number | null>(null);

  const sheetObjects = watch('sheetObjects');
  const latestSheetId = useRef(sheetObjects[sheetObjects.length - 1].id);

  const getNextSheetId = () => {
    latestSheetId.current += 1;
    return latestSheetId.current;
  };

  const [targetSheetIndex, setTargetSheetIndex] = useState(undefined);
  const sheetIds = sheetObjects.map((sheet) => sheet.id);
  const [activeSheetId, setActiveSheetId] = useState(sheetIds[0]);

  const { windowDotConfirm } = useConfirm(
    {
      title: 'Are you sure?',
      message: `If you delete this sheet "${sheetObjects[targetSheetIndex]?.sheetName}", this action cannot be undone.`,
      confirmButtonColor: 'error',
      confirmButtonText: 'Delete',
    },
    [targetSheetIndex]
  );

  const addSheetTab = () => {
    let done = false;
    let i = sheetObjects.length;
    const sheetNames = sheetObjects.map((sheet) => sheet.sheetName.toLowerCase());
    let sheetName;
    while (!done && i < 100) {
      i += 1;
      sheetName = `Untitled Sheet ${i}`;
      const found = sheetNames.includes(sheetName.toLowerCase());
      if (!found) {
        done = true;
      }
    }
    setValue(
      'sheetObjects',
      [
        ...sheetObjects,
        {
          id: getNextSheetId(),
          sheetName,
        },
      ],
      setValueOptions
    );
  };

  const closeSheetTabOptions = () => {
    setSheetTabAnchorIndex(null);
  };

  const showSheetTabOptions = (index) => {
    if (index === sheetTabAnchorIndex) {
      closeSheetTabOptions();
    } else {
      setSheetTabAnchorIndex(index);
    }
  };

  const onTabChange = (sheetId) => {
    setActiveSheetId(sheetId);
    setActiveSheet(sheetObjects.find((item) => item.id === sheetId));
  };

  const deleteSheet = async (deleteIndex) => {
    closeSheetTabOptions();
    setTargetSheetIndex(deleteIndex);
    if (await windowDotConfirm()) {
      const newSheetObjects = sheetObjects.filter((item, index) => index !== deleteIndex);
      setValue('sheetObjects', newSheetObjects, setValueOptions);
      if (sheetIds[deleteIndex] === activeSheetId) {
        onTabChange(newSheetObjects[0].id);
      }
    }
  };

  const [newName, setNewName] = useState('');
  const [helperText, setHelperText] = useState('');
  const [openRenameModal, closeRenameModal] = useModal(
    () => {
      const saveRenamedSheetName = () => {
        const sheetNames = sheetObjects.map((sheet) => sheet.sheetName.toLowerCase());
        if (sheetNames.includes(newName.toLowerCase())) {
          setHelperText('Please choose a unique sheet name.');
          return;
        }

        setValue(`sheetObjects.${targetSheetIndex}.sheetName`, newName, setValueOptions);
        setHelperText('');
        try {
          SheetNameSchema.parse(newName);
        } catch (e) {
          const message = JSON.parse(e.message);
          setHelperText(message[0].message);
          return;
        }

        closeRenameModal();
      };

      return (
        <Stack spacing={2}>
          <Typography variant="h5">Rename Sheet</Typography>
          <TextField
            label="New Sheet Name"
            defaultValue={newName}
            onChange={(e) => setNewName(e.target.value)}
            helperText={helperText}
          />
          <FormEndOptions>
            <Stack direction="row" spacing={2} justifyContent="center">
              <Button variant="outlined" onClick={closeRenameModal}>
                Close
              </Button>
              <Button onClick={saveRenamedSheetName}>Save</Button>
            </Stack>
          </FormEndOptions>
        </Stack>
      );
    },
    null,
    [setValue, newName, setNewName, targetSheetIndex, helperText, setHelperText]
  );

  const renameSheet = (renameIndex) => {
    setTargetSheetIndex(renameIndex);
    const { sheetName } = sheetObjects[renameIndex];
    setHelperText('');
    setNewName(sheetName);
    openRenameModal();
    closeSheetTabOptions();
  };

  const move = (selectedIndex, newIndex) => {
    const targetSheet = sheetObjects[selectedIndex];
    const newSheetObjects = sheetObjects.filter((item, index) => index !== selectedIndex);
    newSheetObjects.splice(newIndex, 0, targetSheet);
    setValue('sheetObjects', newSheetObjects, setValueOptions);
    showSheetTabOptions(newIndex);
    closeSheetTabOptions();
  };

  const moveLeft = (selectedIndex) => {
    // We add sheetObjects.length to the selected index before subtracting because
    // mod % cannot deal with negative numbers, eg -1.
    move(selectedIndex, (selectedIndex + sheetObjects.length - 1) % sheetObjects.length);
  };

  const moveRight = (selectedIndex) => {
    move(selectedIndex, (selectedIndex + 1) % sheetObjects.length);
  };

  const duplicateSheet = (selectedIndex) => {
    const targetSheet = sheetObjects[selectedIndex];
    const duplicatedSheet = {
      ...targetSheet,
      id: getNextSheetId(),
      sheetName: `Copy of ${targetSheet.sheetName}`,
    };
    sheetObjects.splice(selectedIndex + 1, 0, duplicatedSheet);
    setValue('sheetObjects', sheetObjects, setValueOptions);
    closeSheetTabOptions();
  };

  const style1 = {
    bgcolor: '#fff',
    borderRadius: '4px',
    boxShadow: '0px 0px 0px 1px #E0E0E0',
  };

  return (
    <Stack spacing={2}>
      <Stack sx={style1}>
        <Tabs value={activeSheetId} variant="scrollable">
          {sheetObjects.map((sheet, index) => (
            <Tab
              key={sheet.id}
              label={sheet.sheetName}
              value={sheet.id}
              sx={{ textTransform: 'none', py: 0, minHeight: '48px' }}
              onClick={() => onTabChange(sheet.id)}
              icon={
                <Button
                  variant="text"
                  size="small"
                  sx={{ minWidth: 'auto' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    showSheetTabOptions(index);
                  }}
                  aria-label={`Menu for ${sheet.sheetName}`}
                >
                  <FontAwesomeIcon
                    icon={faCaretDown}
                    ref={(element) => {
                      sheetsTabOptionsRef.current[index] = element;
                    }}
                    fontSize="14px"
                    color={activeSheetId !== sheet.id && '#8C8C8C'}
                  />
                </Button>
              }
              iconPosition="end"
            />
          ))}
          <Stack ml="12px" mt="12px">
            <Tooltip title="Add Sheet">
              <Button
                variant="text"
                size="small"
                color="info"
                sx={{ minWidth: 'auto' }}
                onClick={addSheetTab}
                aria-label="Add New Sheet"
              >
                <FontAwesomeIcon icon={faPlus} fontSize="16px" color="#757575" />
              </Button>
            </Tooltip>
          </Stack>
          <Stack>
            <ClickAwayListener onClickAway={closeSheetTabOptions}>
              <div>
                <Popper
                  open={sheetTabAnchorIndex !== null}
                  anchorEl={sheetTabAnchorIndex !== null && sheetsTabOptionsRef.current[sheetTabAnchorIndex]}
                  placement="bottom-end"
                >
                  <Paper sx={{ cursor: 'pointer' }}>
                    <Stack spacing={2} p={2}>
                      <Typography
                        onClick={() => duplicateSheet(sheetTabAnchorIndex)}
                        aria-label={`Duplicate Tab ${sheetTabAnchorIndex}`}
                      >
                        Duplicate
                      </Typography>
                      <Typography
                        onClick={() => renameSheet(sheetTabAnchorIndex)}
                        aria-label={`Rename Tab ${sheetTabAnchorIndex}`}
                      >
                        Rename
                      </Typography>
                    </Stack>
                    {sheetObjects.length > 1 && (
                      <>
                        <Divider />
                        <Stack spacing={2} p={2}>
                          <Typography
                            onClick={() => sheetTabAnchorIndex !== 0 && moveLeft(sheetTabAnchorIndex)}
                            aria-label={`Move Left Tab ${sheetTabAnchorIndex}`}
                            color={sheetTabAnchorIndex === 0 && 'rgba(45, 45, 45, 0.25)'}
                            sx={{ cursor: sheetTabAnchorIndex === 0 && 'default' }}
                          >
                            Move Left
                          </Typography>
                          <Typography
                            onClick={() =>
                              sheetTabAnchorIndex !== sheetObjects.length - 1 && moveRight(sheetTabAnchorIndex)
                            }
                            aria-label={`Move Right Tab ${sheetTabAnchorIndex}`}
                            color={sheetTabAnchorIndex === sheetObjects.length - 1 && 'rgba(45, 45, 45, 0.25)'}
                            sx={{ cursor: sheetTabAnchorIndex === sheetObjects.length - 1 && 'default' }}
                          >
                            Move Right
                          </Typography>
                        </Stack>
                        <Divider />
                        <Stack spacing={2} p={2}>
                          <Typography
                            onClick={() => deleteSheet(sheetTabAnchorIndex)}
                            aria-label={`Delete Tab ${sheetTabAnchorIndex}`}
                            color="error"
                          >
                            Delete
                          </Typography>
                        </Stack>
                      </>
                    )}
                  </Paper>
                </Popper>
              </div>
            </ClickAwayListener>
          </Stack>
        </Tabs>
      </Stack>
      <Stack sx={style1}>
        <SectionsPreview />
      </Stack>
    </Stack>
  );
}

export default Sheets;
